import React, { useMemo, useState } from 'react'
import { Box, Typography, styled } from '@mui/material'
import moment from 'moment'
import { shallow } from 'zustand/shallow'
import { useNavigate } from 'react-router-dom'
import { Button, ChipToggleGroup, EmptyState } from '@/design-system'
import useAppStore from '@/store/useAppStore'
import { ActivityTypeUtil, NotificationItem, SocketEventType } from '@/types/notification'
import { convertToGMT } from '@/utils/helper'
import { NotificationRow } from '../../components/NotificationRow'

const Page = styled(Box)({ maxWidth: 760 })

const PageHead = styled(Box)({ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 })
const H1 = styled(Typography)({ fontSize: 24, fontWeight: 720, letterSpacing: '-0.03em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 13.5, color: 'var(--ds-ink-subtle)', marginTop: 6 })

const FilterRow = styled(Box)({ padding: '22px 0 20px' })

const DayLabel = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--ds-ink-subtle)',
  margin: '26px 2px 10px',
  '&:first-of-type': { marginTop: 0 },
})

const Card = styled(Box)({
  background: 'var(--ds-canvas)',
  borderRadius: 'var(--ds-radius-lg)',
  boxShadow: 'var(--ds-shadow-soft)',
  marginBottom: 8,
  overflow: 'hidden',
  '& > *:not(:first-of-type)': { borderTop: '1px solid var(--ds-border)' },
})

type FilterValue = 'all' | 'unread' | 'comments' | 'people'

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'comments', label: 'Comments & likes' },
  { value: 'people', label: 'People' },
]

function matchesFilter(item: NotificationItem, filter: FilterValue): boolean {
  if (filter === 'all') return true
  if (filter === 'unread') return !item.isRead
  const kind = ActivityTypeUtil.getKind(item.eventType)
  if (filter === 'comments') return kind === 'like' || kind === 'comment'
  if (filter === 'people') return kind === 'follow' || kind === 'request'
  return true
}

/** Today / Yesterday / Earlier this week / Older — items already arrive newest-first. */
function dayBucket(eventDateTime: string): string {
  const d = moment(convertToGMT(eventDateTime))
  const now = moment()
  if (d.isSame(now, 'day')) return 'Today'
  if (d.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday'
  if (d.isAfter(now.clone().subtract(7, 'days'))) return 'Earlier this week'
  return 'Older'
}

function groupByDay(items: NotificationItem[]): { label: string; items: NotificationItem[] }[] {
  const groups: { label: string; items: NotificationItem[] }[] = []
  for (const item of items) {
    const label = dayBucket(item.eventDateTime)
    const last = groups[groups.length - 1]
    if (last && last.label === label) last.items.push(item)
    else groups.push({ label, items: [item] })
  }
  return groups
}

/**
 * `/notification` — replaces the legacy bell-icon dropdown + unstyled flat-list page
 * with one dedicated, day-grouped surface inside the album shell.
 *
 * Notification delivery is WebSocket-only (see useNotificationStorageHook) — there is
 * no history endpoint, so this only ever shows what arrived since the tab opened.
 */
export function NotificationsScreen() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<FilterValue>('all')
  const [items, unreadCount] = useAppStore(
    (state) => [state.notification.items, state.notification.unreadCount],
    shallow,
  )
  const markAsRead = useAppStore((state) => state.markAsRead)
  const markAllAsRead = useAppStore((state) => state.markAllAsRead)

  const filterOptions = useMemo(
    () =>
      FILTER_OPTIONS.map((opt) =>
        opt.value === 'unread' && unreadCount > 0 ? { ...opt, label: `Unread ${unreadCount}` } : opt,
      ),
    [unreadCount],
  )

  const filtered = useMemo(() => items.filter((item) => matchesFilter(item, filter)), [items, filter])
  const groups = useMemo(() => groupByDay(filtered), [filtered])

  const onClickItem = (item: NotificationItem) => {
    markAsRead(item.eventID)
    if (ActivityTypeUtil.isTopicRelatedEvent(item.eventType)) {
      const query = new URLSearchParams()
      query.set('topicID', ActivityTypeUtil.getTopicID(item))
      query.set('topicType', item.topicType.toString())
      query.set('noteID', ActivityTypeUtil.getNoteID(item))
      navigate(`/preview?${query.toString()}`)
      return
    }
    // People events aren't tied to a topic — send them to the person/connections
    // surface instead of doing nothing on click.
    if (item.eventType === SocketEventType.USER_CONNECTED) {
      navigate(`/profile/${item.sourceUserID}`)
      return
    }
    if (item.eventType === SocketEventType.CONNECTION_REQUESTED) {
      navigate('/connections?tab=requests')
    }
  }

  return (
    <Page>
      <PageHead>
        <Box>
          <H1>Notifications</H1>
          <Sub>Everything that happened while you were away.</Sub>
        </Box>
        {unreadCount > 0 && (
          <Button variant='ghost' size='sm' onClick={markAllAsRead}>
            ✓ Mark all read
          </Button>
        )}
      </PageHead>

      <FilterRow>
        <ChipToggleGroup options={filterOptions} value={filter} onChange={(v) => setFilter(v as FilterValue)} />
      </FilterRow>

      {filtered.length === 0 ? (
        <EmptyState
          icon={<span style={{ fontSize: 32, opacity: 0.35 }}>🔔</span>}
          title={items.length === 0 ? "You're all caught up" : 'Nothing here'}
          description={
            items.length === 0
              ? 'New likes, comments, and follows will show up here.'
              : 'No notifications match this filter right now.'
          }
        />
      ) : (
        groups.map((group) => (
          <React.Fragment key={group.label}>
            <DayLabel>{group.label}</DayLabel>
            <Card>
              {group.items.map((item) => (
                <NotificationRow key={item.eventID} item={item} onClick={onClickItem} />
              ))}
            </Card>
          </React.Fragment>
        ))
      )}
    </Page>
  )
}
